/**
 * =========================================================================================
 * SHERGOLD ERP - INVOICE DESIGNER & ASSIGNER IN-APP USER MANUAL MODAL
 * =========================================================================================
 * 
 * Interactive documentation viewer providing simple, step-by-step guidance for:
 * 1. Paper sizing & roll geometries
 * 2. Visual elements, images, barcodes & tables
 * 3. Dynamic system tokens & jewellery symbols
 * 4. Millimeter precision alignment suite & D-Pad
 * 5. Zooming & canvas inspection (up to 400%)
 * 6. Voucher routing & showroom branch assignment
 * 7. Keyboard shortcuts & troubleshooting
 */

import React, { useState } from "react";
import {
  FiBookOpen,
  FiX,
  FiPrinter,
  FiSliders,
  FiMaximize2,
  FiLayers,
  FiCode,
  FiMove,
  FiCheckCircle,
  FiHelpCircle
} from "react-icons/fi";

const INTER = { fontFamily: "Inter, sans-serif" };

export default function InvoiceDesignerManualModal({ isOpen, onClose }) {
  const [activeTab, setActiveTab] = useState("quickstart");

  if (!isOpen) return null;

  const MANUAL_TABS = [
    { id: "quickstart", label: "🚀 Quick Start", icon: FiCheckCircle },
    { id: "paper", label: "📐 Paper & Margins", icon: FiSliders },
    { id: "elements", label: "🧰 Elements & Toolbox", icon: FiLayers },
    { id: "tokens", label: "🏷️ Dynamic Tokens", icon: FiCode },
    { id: "alignment", label: "📏 Alignment & D-Pad", icon: FiMove },
    { id: "zoom", label: "🔍 Zoom & Inspection", icon: FiMaximize2 },
    { id: "assigner", label: "🖨️ Model Assigner", icon: FiPrinter },
    { id: "shortcuts", label: "⌨️ Shortcuts & FAQ", icon: FiHelpCircle },
  ];

  return (
    <div className="fixed inset-0 z-[12000] bg-slate-900/60 backdrop-blur-xs flex items-center justify-center p-3 sm:p-5 select-none animate-fadeIn">
      <div 
        className="bg-white w-full max-w-4xl h-[88vh] max-h-[820px] rounded-2xl shadow-2xl flex flex-col overflow-hidden border border-slate-200"
        style={INTER}
      >
        {/* ── Top Header (Simple & Low-Profile) ─────────────────────────────────── */}
        <div className="bg-[#0f2d59] text-white px-3.5 py-1.5 flex items-center justify-between shrink-0 border-b border-slate-700/50">
          <div className="flex items-center gap-2">
            <FiBookOpen className="w-4 h-4 text-amber-400" />
            <h2 className="text-xs font-semibold text-white tracking-wide">
              Invoice Designer & Assigner — User Guide
            </h2>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="w-6 h-6 rounded text-slate-300 hover:text-white hover:bg-rose-600 flex items-center justify-center transition cursor-pointer active:scale-90"
            title="Close Guide"
          >
            <FiX className="w-4 h-4" />
          </button>
        </div>

        {/* ── Subheader / Navigation Tabs ───────────────────────────────────────── */}
        <div className="bg-slate-50 border-b border-slate-200 px-3 py-1 flex items-center justify-between gap-2 shrink-0">
          <div className="flex items-center gap-1 overflow-x-auto py-0.5 studio-scrollbar flex-1">
            {MANUAL_TABS.map((tab) => {
              const isActive = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => setActiveTab(tab.id)}
                  className={`px-2.5 py-1 rounded-md text-[11px] font-semibold whitespace-nowrap transition cursor-pointer flex items-center gap-1 border ${
                    isActive
                      ? "bg-amber-500 text-white border-amber-600 font-bold shadow-2xs"
                      : "bg-white text-slate-700 border-slate-200 hover:bg-slate-100"
                  }`}
                >
                  <span>{tab.label}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* ── Tab Content Body ──────────────────────────────────────────────────── */}
        <div className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-5 studio-scrollbar text-slate-700 text-xs leading-relaxed">
          
          {/* TAB 1: QUICK START */}
          {activeTab === "quickstart" && (
            <div className="space-y-4 animate-fadeIn">
              <div className="p-4 rounded-xl bg-gradient-to-r from-amber-50 via-amber-50/60 to-orange-50 border border-amber-200">
                <h3 className="text-sm font-bold text-amber-950 flex items-center gap-2 mb-1">
                  <span>🚀 5-Minute Quick Workflow</span>
                </h3>
                <p className="text-[11.5px] text-amber-900 leading-normal">
                  Create, save, and bind a print design to your transactions in four simple steps:
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3.5">
                <div className="p-3.5 rounded-xl border border-slate-200 bg-white shadow-2xs space-y-1.5">
                  <div className="flex items-center gap-2">
                    <span className="w-6 h-6 rounded-full bg-blue-100 text-blue-700 font-bold flex items-center justify-center text-xs">1</span>
                    <strong className="text-slate-900 text-xs">Set Paper & Margins</strong>
                  </div>
                  <p className="text-[11.5px] text-slate-600">
                    In the top bar, choose a preset (e.g., <strong>58MM Thermal</strong> or <strong>A4</strong>). Set width, height, and padding margin (e.g. 2mm or 8mm).
                  </p>
                </div>

                <div className="p-3.5 rounded-xl border border-slate-200 bg-white shadow-2xs space-y-1.5">
                  <div className="flex items-center gap-2">
                    <span className="w-6 h-6 rounded-full bg-emerald-100 text-emerald-700 font-bold flex items-center justify-center text-xs">2</span>
                    <strong className="text-slate-900 text-xs">Add Content & Tokens</strong>
                  </div>
                  <p className="text-[11.5px] text-slate-600">
                    Click <strong>+ Add Text</strong> or <strong>+ Add Table</strong> in the Toolbox. Copy variables like <code>&#123;&#123;CustomerName&#125;&#125;</code> from the Tokens tab and paste into text boxes.
                  </p>
                </div>

                <div className="p-3.5 rounded-xl border border-slate-200 bg-white shadow-2xs space-y-1.5">
                  <div className="flex items-center gap-2">
                    <span className="w-6 h-6 rounded-full bg-amber-100 text-amber-700 font-bold flex items-center justify-center text-xs">3</span>
                    <strong className="text-slate-900 text-xs">Save Your Template</strong>
                  </div>
                  <p className="text-[11.5px] text-slate-600">
                    Click <strong>Save Template</strong> (or press <code>Ctrl+S</code>). Your template is securely saved to both the database and browser storage.
                  </p>
                </div>

                <div className="p-3.5 rounded-xl border border-slate-200 bg-white shadow-2xs space-y-1.5">
                  <div className="flex items-center gap-2">
                    <span className="w-6 h-6 rounded-full bg-purple-100 text-purple-700 font-bold flex items-center justify-center text-xs">4</span>
                    <strong className="text-slate-900 text-xs">Assign to Voucher</strong>
                  </div>
                  <p className="text-[11.5px] text-slate-600">
                    Click <strong>Assign Models</strong>. Pick your voucher type (e.g. <code>SCHEME RECEIPT</code>) and click <strong>Assign Model</strong>. It activates immediately on vouchers!
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* TAB 2: PAPER & MARGINS */}
          {activeTab === "paper" && (
            <div className="space-y-4 animate-fadeIn">
              <h3 className="text-sm font-bold text-slate-900">📐 Paper Sizes & Dimensions Setup</h3>
              <p className="text-slate-600">
                Control the paper sheet geometry in the top toolbar to match your printer hardware:
              </p>

              <div className="space-y-2 border border-slate-200 rounded-xl overflow-hidden bg-white">
                <div className="p-3 bg-slate-50 border-b border-slate-200 font-bold text-slate-800 text-[11px] grid grid-cols-3">
                  <span>Paper Preset</span>
                  <span>Dimensions (W × H)</span>
                  <span>Recommended Usage</span>
                </div>
                <div className="p-3 border-b border-slate-100 grid grid-cols-3 text-[11.5px]">
                  <strong>58MM Thermal</strong>
                  <span>57 × 120 mm (or custom)</span>
                  <span>Compact receipt printers, Scheme slips</span>
                </div>
                <div className="p-3 border-b border-slate-100 grid grid-cols-3 text-[11.5px]">
                  <strong>80MM POS Roll</strong>
                  <span>80 × 160 mm (or custom)</span>
                  <span>Standard POS billing counters</span>
                </div>
                <div className="p-3 border-b border-slate-100 grid grid-cols-3 text-[11.5px]">
                  <strong>A4 Sheet</strong>
                  <span>210 × 297 mm</span>
                  <span>Full GST tax invoices, Old Gold vouchers</span>
                </div>
                <div className="p-3 border-b border-slate-100 grid grid-cols-3 text-[11.5px]">
                  <strong>A5 Half-Sheet</strong>
                  <span>148 × 210 mm</span>
                  <span>Passbook vouchers, compact invoices</span>
                </div>
                <div className="p-3 grid grid-cols-3 text-[11.5px]">
                  <strong>Rat-Tail & RFID Tags</strong>
                  <span>100 × 12 mm / 40 × 25 mm</span>
                  <span>Jewellery barcode tags & ring labels</span>
                </div>
              </div>

              <div className="p-3 rounded-lg bg-blue-50 border border-blue-200 text-blue-900 space-y-1">
                <strong>💡 Margin Padding (Pad mm):</strong>
                <p className="text-[11px]">
                  The Pad input sets the printable margin (typically 2mm for thermal rolls, 8mm for A4). The dashed orange line on the canvas marks this safe area to prevent text from clipping.
                </p>
              </div>
            </div>
          )}

          {/* TAB 3: ELEMENTS & TOOLBOX */}
          {activeTab === "elements" && (
            <div className="space-y-4 animate-fadeIn">
              <h3 className="text-sm font-bold text-slate-900">🧰 Adding & Configuring Elements</h3>
              <p className="text-slate-600">
                Click any tool in the left Toolbox tab to add components to your canvas:
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="p-3 border border-slate-200 rounded-xl bg-white space-y-1">
                  <strong className="text-slate-900">🔤 Text Element:</strong>
                  <p className="text-slate-600">Use for headers, titles, terms, and variable tokens. Double-click or select to edit font size, weight, color, and text alignment.</p>
                </div>
                <div className="p-3 border border-slate-200 rounded-xl bg-white space-y-1">
                  <strong className="text-slate-900">🖼️ Logo Image:</strong>
                  <p className="text-slate-600">Upload store logo, hallmark seals, or BIS stamps. High-contrast PNG logos produce the sharpest thermal prints.</p>
                </div>
                <div className="p-3 border border-slate-200 rounded-xl bg-white space-y-1">
                  <strong className="text-slate-900">📊 Data Table:</strong>
                  <p className="text-slate-600">Multi-column itemized tables for products, weight, purity, rate, and amount with automatic header and row styling.</p>
                </div>
                <div className="p-3 border border-slate-200 rounded-xl bg-white space-y-1">
                  <strong className="text-slate-900">📱 QR Code:</strong>
                  <p className="text-slate-600">Dynamic UPI payment QR codes or authentication links scanned directly by customer phone cameras.</p>
                </div>
                <div className="p-3 border border-slate-200 rounded-xl bg-white space-y-1">
                  <strong className="text-slate-900">🏷️ Code128 Barcode:</strong>
                  <p className="text-slate-600">Standard item or voucher barcode readable by laser handheld barcode scanners.</p>
                </div>
                <div className="p-3 border border-slate-200 rounded-xl bg-white space-y-1">
                  <strong className="text-slate-900">➖ Dividers &amp; Boxes:</strong>
                  <p className="text-slate-600">Horizontal separator lines and outlined frames for summary sections and totals.</p>
                </div>
              </div>

              <div className="p-3 rounded-lg bg-emerald-50 border border-emerald-200 text-emerald-950 space-y-1">
                <strong>💡 Logos &amp; QR Codes Best Practices:</strong>
                <ul className="list-disc list-inside space-y-0.5 text-[11px] text-emerald-900">
                  <li><strong>Store Logo:</strong> Click <em>Upload Store Logo</em> in the Toolbox to pick any PNG, JPG, or SVG emblem. It is embedded directly into your template definition.</li>
                  <li><strong>Dynamic UPI QR:</strong> Use the pre-configured <em>Dynamic UPI QR Code</em> button. It automatically evaluates <code>&#123;&#123;TotalAmount&#125;&#125;</code> and <code>&#123;&#123;UpiId&#125;&#125;</code> so customers can scan and pay instantly via Google Pay, PhonePe, or Paytm.</li>
                  <li><strong>Website / Verification QR:</strong> Generates a QR link directly to your online e-invoice verification page or company portal.</li>
                </ul>
              </div>
            </div>
          )}

          {/* TAB 4: DYNAMIC TOKENS */}
          {activeTab === "tokens" && (
            <div className="space-y-4 animate-fadeIn">
              <h3 className="text-sm font-bold text-slate-900">🏷️ Dynamic System Tokens</h3>
              <p className="text-slate-600">
                Tokens are dynamic placeholders automatically replaced with actual voucher numbers, customer names, and gold weights during printing.
              </p>

              <div className="p-3 bg-amber-50 border border-amber-200 rounded-xl text-amber-950 text-[11.5px] space-y-1">
                <strong>How to use Tokens:</strong>
                <p>
                  1. Open the <strong>Tokens tab</strong> on the right panel.<br/>
                  2. Click any token chip (e.g. <code>&#123;&#123;CustomerName&#125;&#125;</code>) to copy it.<br/>
                  3. Select a text element on the canvas and paste (<code>Ctrl+V</code>) inside it.
                </p>
              </div>

              <div className="space-y-2">
                <strong className="text-slate-900 text-xs block">Popular Tokens Reference:</strong>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  <span className="p-2 rounded bg-slate-50 border border-slate-200 font-mono text-[10.5px]"><code>&#123;&#123;CustomerName&#125;&#125;</code></span>
                  <span className="p-2 rounded bg-slate-50 border border-slate-200 font-mono text-[10.5px]"><code>&#123;&#123;TotalAmount&#125;&#125;</code></span>
                  <span className="p-2 rounded bg-slate-50 border border-slate-200 font-mono text-[10.5px]"><code>&#123;&#123;AmountInWords&#125;&#125;</code></span>
                  <span className="p-2 rounded bg-slate-50 border border-slate-200 font-mono text-[10.5px]"><code>&#123;&#123;VoucherNo&#125;&#125;</code></span>
                  <span className="p-2 rounded bg-slate-50 border border-slate-200 font-mono text-[10.5px]"><code>&#123;&#123;Date&#125;&#125;</code></span>
                  <span className="p-2 rounded bg-slate-50 border border-slate-200 font-mono text-[10.5px]"><code>&#123;&#123;NetWeight&#125;&#125;</code></span>
                  <span className="p-2 rounded bg-slate-50 border border-slate-200 font-mono text-[10.5px]"><code>&#123;&#123;PureWeight&#125;&#125;</code></span>
                  <span className="p-2 rounded bg-slate-50 border border-slate-200 font-mono text-[10.5px]"><code>&#123;&#123;Rate&#125;&#125;</code></span>
                  <span className="p-2 rounded bg-slate-50 border border-slate-200 font-mono text-[10.5px]"><code>&#123;&#123;CompanyName&#125;&#125;</code></span>
                </div>
              </div>
            </div>
          )}

          {/* TAB 5: ALIGNMENT & D-PAD */}
          {activeTab === "alignment" && (
            <div className="space-y-4 animate-fadeIn">
              <h3 className="text-sm font-bold text-slate-900">📏 Precision Alignment Suite & Micro-Nudge D-Pad</h3>
              <p className="text-slate-600">
                Found under <strong>Inspector Tab 3 (Styles & Layout)</strong>:
              </p>

              <div className="space-y-3">
                <div className="p-3 border border-slate-200 rounded-xl bg-white space-y-1.5">
                  <strong className="text-slate-900">1. Numeric Inputs (X, Y, W, H in mm):</strong>
                  <p className="text-slate-600">
                    Type exact millimeters or click the up/down arrows to position items with sub-millimeter precision.
                  </p>
                </div>

                <div className="p-3 border border-slate-200 rounded-xl bg-white space-y-1.5">
                  <strong className="text-slate-900">2. One-Click Page Alignment:</strong>
                  <ul className="list-disc list-inside space-y-0.5 text-slate-600 pl-1">
                    <li><strong>Center Page Horizontally</strong>: Centers item between paper left and right.</li>
                    <li><strong>Fit Page Width</strong>: Expands item width to fill printable space between margins.</li>
                    <li><strong>Left / Right Margins</strong>: Snaps flush to the margin safe-zone boundaries.</li>
                  </ul>
                </div>

                <div className="p-3 border border-slate-200 rounded-xl bg-white space-y-1.5">
                  <strong className="text-slate-900">3. Micro-Nudge D-Pad:</strong>
                  <p className="text-slate-600">
                    Nudge items Up, Down, Left, or Right with selectable step chips: <code>0.1mm</code>, <code>0.5mm</code>, <code>1.0mm</code>, or <code>2.0mm</code>.
                  </p>
                </div>

                <div className="p-3 border border-slate-200 rounded-xl bg-white space-y-1.5">
                  <strong className="text-slate-900">4. Multi-Selection Suite (Hold Shift + Click):</strong>
                  <p className="text-slate-600">
                    Select 2 or more elements to reveal group alignment: align common Lefts, Centers, Rights, Tops, Bottoms, equal vertical/horizontal spacing, and matching sizes.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* TAB 6: ZOOM & INSPECTION */}
          {activeTab === "zoom" && (
            <div className="space-y-4 animate-fadeIn">
              <h3 className="text-sm font-bold text-slate-900">🔍 High-Precision Zoom (Up to 400%)</h3>
              <p className="text-slate-600">
                Essential for inspecting tiny jewellery barcode tags (100×12mm) and 58mm thermal receipts close-up:
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="p-3.5 border border-slate-200 rounded-xl bg-white space-y-1">
                  <strong className="text-slate-900">Zoom Controls:</strong>
                  <p className="text-slate-600">
                    Use <code>[-]</code> and <code>[+]</code> in the top viewport bar or click preset chips (<code>50%</code>, <code>100%</code>, <code>200%</code>, <code>300%</code>, <code>400%</code>).
                  </p>
                </div>
                <div className="p-3.5 border border-slate-200 rounded-xl bg-white space-y-1">
                  <strong className="text-slate-900">1:1 Quick Reset:</strong>
                  <p className="text-slate-600">
                    Click the <strong>1:1</strong> button (or press <code>Ctrl+0</code>) to instantly snap back to standard 100% view.
                  </p>
                </div>
                <div className="p-3.5 border border-slate-200 rounded-xl bg-white space-y-1 sm:col-span-2">
                  <strong className="text-slate-900">Smooth Scrollbar Navigation:</strong>
                  <p className="text-slate-600">
                    When zooming in to 200% or 400%, the canvas container dynamically enables smooth scrollbars, letting you scroll all the way down to inspect footers and totals without clipping.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* TAB 7: MODEL ASSIGNER */}
          {activeTab === "assigner" && (
            <div className="space-y-4 animate-fadeIn">
              <h3 className="text-sm font-bold text-slate-900">🖨️ Voucher Print Model Assigner Hub</h3>
              <p className="text-slate-600">
                Connects your designs to transaction vouchers across different showrooms:
              </p>

              <div className="p-3.5 bg-emerald-50 border border-emerald-200 rounded-xl text-emerald-950 space-y-2">
                <strong>How to Assign a Model:</strong>
                <ol className="list-decimal list-inside space-y-1 text-[11.5px]">
                  <li>Open <strong>Print Model Assigner</strong> (via top bar or Tools menu).</li>
                  <li>Pick your <strong>Showroom Location</strong> (e.g. <code>Global Default (All Branches)</code> or a specific branch).</li>
                  <li>In the left list, select the <strong>Voucher Type</strong> (e.g. <code>SCHEME RECEIPT</code>).</li>
                  <li>Click your desired <strong>Template Card</strong>. Check the live PDF preview in the right pane.</li>
                  <li>Click <strong>Assign Model</strong>.</li>
                </ol>
              </div>

              <div className="p-3 border border-slate-200 rounded-xl bg-white space-y-1 text-slate-600">
                <strong className="text-slate-900">Instant Real-Time Reflection:</strong>
                <p>
                  Any assignment or update is broadcasted immediately via <code>shergold_print_sync</code>. The next time you click Print on that voucher, your new design prints instantly without requiring a page reload!
                </p>
              </div>
            </div>
          )}

          {/* TAB 8: SHORTCUTS & FAQ */}
          {activeTab === "shortcuts" && (
            <div className="space-y-4 animate-fadeIn">
              <h3 className="text-sm font-bold text-slate-900">⌨️ Keyboard Shortcuts & FAQ</h3>

              <div className="border border-slate-200 rounded-xl overflow-hidden bg-white">
                <div className="p-2.5 bg-slate-50 border-b border-slate-200 font-bold text-slate-800 text-[11px] grid grid-cols-2">
                  <span>Shortcut</span>
                  <span>Action</span>
                </div>
                <div className="p-2.5 border-b border-slate-100 grid grid-cols-2 text-[11.5px]">
                  <kbd className="font-mono bg-slate-100 px-1.5 py-0.5 rounded border border-slate-300 w-max">Ctrl + S</kbd>
                  <span>Save Template to Database & LocalStorage</span>
                </div>
                <div className="p-2.5 border-b border-slate-100 grid grid-cols-2 text-[11.5px]">
                  <kbd className="font-mono bg-slate-100 px-1.5 py-0.5 rounded border border-slate-300 w-max">Ctrl + + / Ctrl + =</kbd>
                  <span>Zoom In (Step +25%)</span>
                </div>
                <div className="p-2.5 border-b border-slate-100 grid grid-cols-2 text-[11.5px]">
                  <kbd className="font-mono bg-slate-100 px-1.5 py-0.5 rounded border border-slate-300 w-max">Ctrl + -</kbd>
                  <span>Zoom Out (Step -25%)</span>
                </div>
                <div className="p-2.5 border-b border-slate-100 grid grid-cols-2 text-[11.5px]">
                  <kbd className="font-mono bg-slate-100 px-1.5 py-0.5 rounded border border-slate-300 w-max">Ctrl + 0</kbd>
                  <span>Reset Zoom to 100% (1:1 Ratio)</span>
                </div>
                <div className="p-2.5 border-b border-slate-100 grid grid-cols-2 text-[11.5px]">
                  <kbd className="font-mono bg-slate-100 px-1.5 py-0.5 rounded border border-slate-300 w-max">Shift + Click</kbd>
                  <span>Multi-select multiple elements for group alignment</span>
                </div>
                <div className="p-2.5 border-b border-slate-100 grid grid-cols-2 text-[11.5px]">
                  <kbd className="font-mono bg-slate-100 px-1.5 py-0.5 rounded border border-slate-300 w-max">Arrow Keys</kbd>
                  <span>Micro-nudge selected element position</span>
                </div>
                <div className="p-2.5 grid grid-cols-2 text-[11.5px]">
                  <kbd className="font-mono bg-slate-100 px-1.5 py-0.5 rounded border border-slate-300 w-max">Delete / Backspace</kbd>
                  <span>Delete selected element</span>
                </div>
              </div>

              <div className="space-y-2 pt-2">
                <strong className="text-slate-900 text-xs block">Frequently Asked Questions:</strong>
                <div className="p-3 border border-slate-200 rounded-xl bg-white space-y-1">
                  <strong className="text-slate-900 text-[11.5px]">Q: I updated a design, but my voucher printed the old layout.</strong>
                  <p className="text-slate-600">
                    A: Make sure you clicked <strong>Save Template</strong>. The print engine automatically wipes in-memory caches and updates active assignments.
                  </p>
                </div>
                <div className="p-3 border border-slate-200 rounded-xl bg-white space-y-1">
                  <strong className="text-slate-900 text-[11.5px]">Q: Can different branches print different designs?</strong>
                  <p className="text-slate-600">
                    A: Yes! In the Print Model Assigner, switch the Showroom dropdown to the desired branch and assign the template. Branches without a specific assignment automatically inherit the Global Default.
                  </p>
                </div>
              </div>
            </div>
          )}

        </div>

        {/* ── Bottom Action Footer ──────────────────────────────────────────────── */}
        <div className="bg-slate-50 border-t border-slate-200 px-5 py-3 flex items-center justify-between shrink-0">
          <span className="text-[11px] text-slate-500 font-medium">
            SherGold Prime ERP Print Engine • Documentation Guide
          </span>
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-1.5 bg-slate-800 hover:bg-slate-900 text-white rounded-lg text-xs font-bold transition cursor-pointer"
          >
            Got It, Close Guide
          </button>
        </div>

      </div>
    </div>
  );
}
